import React, { useRef, useState, Suspense } from "react";
import { useModalContext } from "../components/ModalRenderer";
import { createDeferredPromise } from "../util";
import useEventCallback from "./useEventCallback";
import useOnUnmount from "./useOnUnmount";
import useModuleInfo from "./useModuleInfo";
import UpgradeModal from "../components/FeatureGate/UpgradeModal.jsx";
import usePlanLimits from "./usePlanLimits";
import useShortCode from "./useShortCode";
import { isExpiredTrialModule } from "@/app/settings/components/Addons/useAddonsState.helpers.js";

let i = 0;

function useModal({ cleanupOnUnmount = true } = {}) {
  const ctx = useModalContext();
  const cleanupFns = useRef(new Set());

  useOnUnmount(() => {
    if (cleanupOnUnmount) [...cleanupFns.current].forEach((cb) => cb());
  });

  const shortCode = useShortCode();

  return useEventCallback(
    (
      node,
      opts = { feature: null, allowInTrial: true, shouldBeSubscribed: false },
    ) => {
      const mountNode = document.createElement("div");
      document.body.appendChild(mountNode);
      const closePromise = createDeferredPromise();

      const ModalState = shortCode
        ? ConnectedModalState
        : UnconnectedModalState;

      const modal = (
        <ModalState
          node={node}
          opts={opts}
          onClosed={handleclosed}
          closePromise={closePromise}
          key={(++i).toString()}
        />
      );

      function handleclosed() {
        ctx.unmountModal(modal);
        cleanupFns.current.delete(handleclosed);
        closePromise.resolve();
      }

      ctx.renderModal(modal);
      cleanupFns.current.add(handleclosed);

      return { close: handleclosed, closed: Promise.resolve(closePromise) };
    },
  );
}

function UnconnectedModalState({ node, onClosed, closePromise }) {
  const [open, setOpen] = useState(true);
  const closeHandler = () => setOpen(false);

  return (
    <Suspense fallback={<div />}>
      {React.cloneElement(node, {
        open,
        close: closeHandler,
        onClosed,
        closePromise,
      })}
    </Suspense>
  );
}

function ConnectedModalState({ node, opts, onClosed, closePromise }) {
  const features = usePlanLimits();
  const info = useModuleInfo();
  const [open, setOpen] = useState(true);
  const closeHandler = () => setOpen(false);

  const isFeatureEnabled = !opts.feature || features[opts.feature];
  const isSubscribed = info?.module?.isSubscribed;
  const isExpiredTrial = isExpiredTrialModule(info?.module);

  const shouldShowUpgradeModal =
    (opts.shouldBeSubscribed &&
      !isSubscribed &&
      !isExpiredTrial &&
      !opts.allowInTrial) ||
    (!opts.allowInTrial && !isSubscribed) ||
    !isFeatureEnabled;

  const modalContent = shouldShowUpgradeModal ? <UpgradeModal /> : node;
  const modalProps = { open, close: closeHandler, onClosed, closePromise };

  return (
    <Suspense fallback={<div />}>
      {React.cloneElement(modalContent, modalProps)}
    </Suspense>
  );
}

export default useModal;
